import { Text } from "@mantine/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDebouncedValue, useCounter } from "@mantine/hooks";
import { useNavigate } from "react-router";
import dayjs from "dayjs";
import { getMonthlyReport, getMonthlyReportExcel } from "../../api/reportsApi";
import { useAuth } from "../../context/AuthContext";
import { errorNotification } from "../../utils/useNotification";
import { downloadBlobFile } from "../../utils/downloadFile";
import CustomSelect from "../common/Inputs/CustomSelect";
import DataTable, { Column } from "../common/Table/DataTable";
import ReportFilterBar from "./ReportFilterBar";
import {
  CountPill,
  EmployeeCell,
  LabelCell,
  PlainCell,
} from "./ReportCells";
import { ResultCount, sortHeader } from "./reportTableUtils";

/** Sunucunun beklediği tarih biçimi (uçlar `new Date(...)` ile ayrıştırıyor). */
const API_DATE_FORMAT = "YYYY-MM-DDTHH:mm:ss[Z]";

/**
 * Sıralanabilir kolonlar → uçların ayrı ayrı beklediği sıralama parametreleri.
 *
 * Eskiden bunlar beş ayrı state'ti ve her başlık tıklamasında diğer dördü elle
 * `""`e çekiliyordu; biri unutulduğunda iki sıralama birden gönderiliyordu.
 * Artık tek bir "aktif kolon" tutuluyor.
 */
const SORT_PARAMS: Record<string, string> = {
  name: "nameSort",
  totalWorkHours: "totalWorkHourSort",
  daysArrived: "dayArrivedSort",
  lateDays: "lateDateSort",
  earlyLeaveDays: "earlyLeaveSort",
};

type Sort = { key: string; dir: "asc" | "desc" } | null;

/** "Güncelle"ye basılana kadar isteğe gitmeyen alanlar. */
interface AppliedRange {
  start: string | null;
  end: string | null;
  entryBuffer: number;
  exitBuffer: number;
}

const ReportTable = () => {
  const navigate = useNavigate();
  const { departmentSelectData, tagSelectData } = useAuth();

  const tagOptions = useMemo(
    () => [...(tagSelectData?.role ?? []), ...(tagSelectData?.skill ?? [])],
    [tagSelectData]
  );

  // Rapor uçları etiketi yalnızca adıyla döndürüyor; rozet rengi/türü bu
  // eşlemeden çözülüyor (bkz. LabelCell).
  const tagLookup = useMemo(
    () => new Map(tagOptions.map((tag: any) => [tag.label, tag])),
    [tagOptions]
  );

  // Üst şerit (taslak) değerleri
  const [startMonth, setStartMonth] = useState<string | null>(
    dayjs().startOf("month").format("YYYY-MM-DD")
  );
  const [endMonth, setEndMonth] = useState<string | null>(
    dayjs().startOf("month").format("YYYY-MM-DD")
  );
  const [entryBuffer, entryCounter] = useCounter(0, { min: 0 });
  const [exitBuffer, exitCounter] = useCounter(0, { min: 0 });

  // İsteğe giden (onaylanmış) değerler
  const [applied, setApplied] = useState<AppliedRange>({
    start: dayjs().startOf("month").format("YYYY-MM-DD"),
    end: dayjs().startOf("month").format("YYYY-MM-DD"),
    entryBuffer: 0,
    exitBuffer: 0,
  });

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [sort, setSort] = useState<Sort>(null);
  const [department, setDepartment] = useState<string | undefined>();
  const [tag, setTag] = useState<string | undefined>();
  const [activeness, setActiveness] = useState<boolean | undefined>();

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  // "Güncelle" butonunun kendi göstergesi: sayfa değiştirirken de dönmesin.
  const [applying, setApplying] = useState(false);
  const [exporting, setExporting] = useState(false);

  const requestParams = useCallback(
    () => ({
      startDate: dayjs(applied.start)
        .startOf("month")
        .format(API_DATE_FORMAT),
      // Bitiş ayının tamamı rapora girsin diye ayın sonuna çekiliyor.
      endDate: dayjs(applied.end).endOf("month").format(API_DATE_FORMAT),
      entryBuffer: applied.entryBuffer,
      exitBuffer: applied.exitBuffer,
      name: debouncedSearch || undefined,
      department,
      tag,
      isActive: activeness,
      ...(sort ? { [SORT_PARAMS[sort.key]]: sort.dir } : {}),
    }),
    [applied, debouncedSearch, department, tag, activeness, sort]
  );

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const { data }: any = await getMonthlyReport({
        ...requestParams(),
        page,
        limit,
      });
      setRows(data?.data ?? []);
      setTotalPages(data?.meta?.totalPages ?? 1);
      setTotalItems(data?.meta?.total ?? 0);
    } catch (error) {
      // Eskiden hata sessizce yutuluyordu: kullanıcı boş tabloya bakıp
      // "kayıt yok" sanıyordu.
      console.error("Aylık rapor alınamadı:", error);
      setRows([]);
      setTotalPages(1);
      setTotalItems(0);
      errorNotification({
        title: "Rapor Yüklenemedi.",
        message: "Aylık rapor alınırken bir hata oluştu.",
      });
    } finally {
      setLoading(false);
      setApplying(false);
    }
  }, [requestParams, page, limit]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    navigate(`?page=${page}&limit=${limit}`, { replace: true });
  }, [page, limit, navigate]);

  /** Filtre/arama değişiminde ilk sayfaya dön. */
  const resetToFirstPage = () => setPage(1);

  const applyRange = () => {
    if (dayjs(startMonth).isAfter(dayjs(endMonth), "month")) {
      errorNotification({
        title: "Geçersiz Tarih Aralığı.",
        message: "Başlangıç ayı, bitiş ayından sonra olamaz.",
      });
      return;
    }
    setApplying(true);
    resetToFirstPage();
    setApplied({
      start: startMonth,
      end: endMonth,
      entryBuffer,
      exitBuffer,
    });
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const { data }: any = await getMonthlyReportExcel(requestParams());
      downloadBlobFile(data, "aylik-rapor");
    } catch (error) {
      console.error("Excel dosyası indirilemedi:", error);
      errorNotification({
        title: "İndirme Başarısız.",
        message: "Excel dosyası oluşturulurken bir hata oluştu.",
      });
    } finally {
      setExporting(false);
    }
  };

  const toggleSort = (key: string) => {
    resetToFirstPage();
    setSort((prev) =>
      prev?.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
  };

  const columns: Column[] = [
    {
      key: "userName",
      header: sortHeader("Ad Soyad", sort, "name"),
      width: "19%",
      onHeaderClick: () => toggleSort("name"),
      render: (row) => <EmployeeCell name={row.userName} />,
    },
    {
      key: "department",
      header: "Departman",
      width: "11%",
      render: (row) => <LabelCell value={row.department} kind="department" />,
    },
    {
      key: "tags",
      header: "Roller",
      width: "12%",
      render: (row) => (
        <LabelCell value={row.tags} refs={row.tagRefs} kind="role" lookup={tagLookup} />
      ),
    },
    {
      key: "totalWorkHours",
      header: sortHeader("Toplam Saat", sort, "totalWorkHours"),
      width: "12%",
      onHeaderClick: () => toggleSort("totalWorkHours"),
      render: (row) => (
        <PlainCell>
          {(row.totalWorkHours ?? 0).toFixed(2)}{" "}
          <Text span size="sm" c="dimmed">
            sa
          </Text>
        </PlainCell>
      ),
    },
    {
      key: "daysArrived",
      header: sortHeader("Gelinen Gün", sort, "daysArrived"),
      width: "12%",
      onHeaderClick: () => toggleSort("daysArrived"),
      render: (row) => <PlainCell>{row.daysArrived ?? 0}</PlainCell>,
    },
    {
      key: "lateDays",
      header: sortHeader("Geç Kalınan", sort, "lateDays"),
      width: "12%",
      onHeaderClick: () => toggleSort("lateDays"),
      render: (row) => (
        <CountPill
          value={row.lateDays}
          title="Vardiya başlangıcı + giriş toleransından sonra okutulan gün sayısı."
        />
      ),
    },
    {
      key: "earlyLeaveDays",
      header: sortHeader("Erken Çıkılan", sort, "earlyLeaveDays"),
      width: "13%",
      onHeaderClick: () => toggleSort("earlyLeaveDays"),
      render: (row) => (
        <CountPill
          value={row.earlyLeaveDays}
          title="Vardiya bitişi - çıkış toleransından önce okutulan gün sayısı."
        />
      ),
    },
    {
      // Kaç günün saati ÖLÇÜLMEDİ. Toplam saate bakan İK'nın "bu sayı ne kadar
      // gerçek?" sorusunun cevabı — bu yüzden uyarı değil, nötr sarı:
      // personelin kabahati değil, ölçüm eksikliği.
      key: "missingExitDays",
      header: "Eksik Çıkış",
      width: "9%",
      render: (row) => (
        <CountPill
          value={row.missingExitDays}
          tone="warn"
          title="Bu günlerde çıkış okutulmamış ve düzeltilmemiş; o günlerin saati toplama HİÇ girmiyor."
        />
      ),
    },
    {
      /*
       * DÜZELTİLEN gün sayısı (plan 2.49-B) — "Eksik Çıkış"tan AYRI sayaç.
       *
       * İkisi toplanmıyor, çünkü biri İK'nın kapatacağı işi, diğeri kapattığı
       * işi sayıyor. Tek rakama düşselerdi "160 saatin ne kadarı ölçüm, ne
       * kadarı beyan" sorusu cevapsız kalırdı; ay kapanışı ileride gündeme
       * gelirse kapı kuralını tam bu ayrım taşıyacak.
       */
      key: "correctedDays",
      header: "Düzeltilen",
      width: "9%",
      render: (row) => (
        <CountPill
          value={row.correctedDays}
          tone="ok"
          title="Çıkışı okutulmamış ama İK'nın beyanıyla kapatılmış gün sayısı; süre beyan edilen çıkışa göre sayıldı."
        />
      ),
    },
  ];

  return (
    <>
      <ReportFilterBar
        mode="month"
        startDate={startMonth}
        endDate={endMonth}
        onStartDateChange={setStartMonth}
        onEndDateChange={setEndMonth}
        entryBuffer={entryBuffer}
        exitBuffer={exitBuffer}
        entryCounter={entryCounter}
        exitCounter={exitCounter}
        onApply={applyRange}
        applying={applying}
        onExport={exportExcel}
        exporting={exporting}
      />

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        rowKey={(row: any) => row.userId}
        emptyText="Seçilen aralıkta kayıt bulunamadı."
        searchValue={search}
        searchPlaceholder="Personel ara..."
        onSearchChange={(value) => {
          resetToFirstPage();
          setSearch(value);
        }}
        toolbar={
          <>
            <CustomSelect
              placeholder="Departman"
              data={departmentSelectData}
              clearable
              onChange={(value: any) => {
                resetToFirstPage();
                setDepartment(value ?? undefined);
              }}
            />
            <CustomSelect
              placeholder="Rol / Yetenek"
              data={tagOptions}
              clearable
              onChange={(value: any) => {
                resetToFirstPage();
                setTag(value ?? undefined);
              }}
            />
            <CustomSelect
              placeholder="Çalışma Durumu"
              data={[
                { label: "Çalışıyor", value: "active" },
                { label: "Ayrıldı", value: "inactive" },
              ]}
              clearable
              onChange={(value: any) => {
                resetToFirstPage();
                setActiveness(
                  value === "active" ? true : value === "inactive" ? false : undefined
                );
              }}
            />
          </>
        }
        subToolbar={<ResultCount total={totalItems} />}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        limit={limit}
        onLimitChange={(value) => {
          setPage(1);
          setLimit(value);
        }}
      />
    </>
  );
};

export default ReportTable;
